import { useEffect, useState } from "react";
import { Input, useFormikContext } from "collections/Form";
import { PolyIcon } from "elements";
import { useCheckSubnetRuleValidityQuery } from "generated/graphql";
import { Maybe, SubnetRuleValidityResult } from "generated/graphql.schema";
import { isValidCIDR } from "./CidrValidation";
import { SubnetMap, SubnetRule } from "./Types";
import { WarningMessage } from "./WarningMessage";
import { useTranslation } from "utilities/i18n";
import {
  getSubnetValue,
  hasDuplicates,
  isValidSubnetRule,
  subnetCidrsMatch
} from "./SubnetHelpers";
import { useTenantId } from "polyrouter/SelectedTenantProvider";

const SubnetRow: React.FC<{
  cidr: string;
  shouldValidate?: boolean;
  fieldName: string;
  index: number;
  isEditable: boolean;
  isPublic: boolean;
  remove: (i: number) => void;
  subnetRule: SubnetRule;
}> = ({
  cidr,
  shouldValidate = true,
  fieldName,
  index,
  isEditable,
  isPublic,
  remove,
  subnetRule
}) => {
  const {
    getFieldMeta,
    initialValues,
    validateForm,
    values
  } = useFormikContext<SubnetMap>();
  const { t } = useTranslation();
  const [validationResponse, setValidationResponse] = useState<{
    result: SubnetRuleValidityResult;
    siteName?: string;
  }>();

  const tenantId = useTenantId();
  const { data, fetching } = useCheckSubnetRuleValidityQuery({
    variables: {
      input: {
        tenantId,
        siteId: subnetRule.siteId,
        ruleId: subnetRule.ruleId.startsWith("new-") ? "" : subnetRule.ruleId,
        publicCidr: subnetRule.publicCidr,
        privateCidr: subnetRule.privateCidr
      }
    },
    pause:
      !shouldValidate ||
      !isValidSubnetRule(subnetRule) ||
      subnetRule.privateCidr === ""
  });

  const inputName = fieldName + ".cidr";
  const { error: fieldError } = getFieldMeta(inputName);

  useEffect(() => {
    let subnetValidity: Maybe<SubnetRuleValidityResult> | undefined =
      SubnetRuleValidityResult.Valid;
    if (shouldValidate) {
      // API will check db for duplicate subnets
      // but even if it doesn't find them, user might have added same subnets on the form (not yet in the db)
      subnetValidity = data?.getSubnetRuleValidity?.result;
      // do the additional validation for this subnet rule only if it was changed or it is new rule
      const initialRule = getSubnetValue(initialValues, subnetRule);
      const currentRule = getSubnetValue(values, subnetRule);

      const isChanged =
        initialRule &&
        currentRule &&
        !subnetCidrsMatch(currentRule, initialRule);
      const isNew = currentRule && !initialRule;

      if (!fetching && (isChanged || isNew)) {
        if (subnetValidity !== SubnetRuleValidityResult.Duplicate) {
          if (
            subnetValidity === SubnetRuleValidityResult.Overlap &&
            data?.getSubnetRuleValidity?.ruleId &&
            data?.getSubnetRuleValidity?.siteId
          ) {
            // check if the rule it overlapped with has been deleted.
            const { ruleId, siteId } = data.getSubnetRuleValidity;

            if (
              subnetRule.siteId === siteId &&
              !getSubnetValue(values, { ruleId, siteId })
            ) {
              subnetValidity = SubnetRuleValidityResult.Valid;
            }
          }

          // check whether the new/changed rule actually conflicts with current values
          if (hasDuplicates(values, subnetRule)) {
            subnetValidity = SubnetRuleValidityResult.Duplicate;
          }
        } else {
          // if the duplicate rule is from this site
          // check whether it's actually been edited or deleted
          if (
            subnetRule.siteId === data?.getSubnetRuleValidity?.siteId &&
            !hasDuplicates(values, subnetRule)
          ) {
            subnetValidity = SubnetRuleValidityResult.Valid;
          }
        }
      }
    }

    if (subnetValidity && validationResponse?.result !== subnetValidity) {
      setValidationResponse({
        result: subnetValidity,
        siteName: data?.getSubnetRuleValidity?.siteName ?? ""
      });
    }
  }, [
    shouldValidate,
    fetching,
    data?.getSubnetRuleValidity,
    subnetRule,
    validationResponse,
    values,
    initialValues
  ]);

  useEffect(() => {
    validateForm();
  }, [validateForm, validationResponse]);

  const validate = () => {
    let error;
    if (validationResponse?.result === SubnetRuleValidityResult.Duplicate) {
      error = "SUBNET_DUPLICATE_ERROR";
    }

    return error;
  };

  const labelClass = ["col-2 label-plain"];
  const buttonClass = ["btn-poly-icon", "text-left", "ml-5"];
  if (!isValidCIDR(cidr)) {
    labelClass.push("v-top");
    buttonClass.push("v-top");
  }

  if (!isPublic) {
    labelClass.push("p-0 offset-2");
  }

  return (
    <>
      <label
        className={labelClass.filter(c => !!c).join(" ")}
        htmlFor={cidr + "-input"}
      >
        {isPublic ? t("PUBLIC_SUBNET") : t("PRIVATE_SUBNET")}:
      </label>
      <div className="v-center p-0 col-2">
        <Input
          name={inputName}
          placeholder={isPublic ? "123.0.0.0/24" : "10.0.0.0/24"}
          disabled={!isEditable}
          validate={validate}
          showErrorMessage={fieldError !== "SUBNET_DUPLICATE_ERROR"}
        />
      </div>
      {isEditable && (
        <button
          type="button"
          className={buttonClass.filter(c => !!c).join(" ")}
          onClick={() => {
            remove(index);
          }}
          data-testid={`clear-${fieldName}`}
        >
          <PolyIcon icon="clear_x" className="icon-clear" />
        </button>
      )}
      {isValidCIDR(cidr) && shouldValidate && (
        <WarningMessage type={validationResponse} />
      )}
    </>
  );
};

export default SubnetRow;
